import { useCallback, useEffect, useRef, useState } from "react";

interface UseImageUploadReturn {
	uploadedImages: Array<{ id: string; file: File; preview: string }>;
	handleImageUpload: (files: File[]) => string;
	removeUploadedImage: (id: string) => void;
	clearUploadedImages: () => void;
}

export const useImageUpload = (): UseImageUploadReturn => {
	const [uploadedImages, setUploadedImages] = useState<
		Array<{ id: string; file: File; preview: string }>
	>([]);

	const uploadedImagesRef = useRef(uploadedImages);
	uploadedImagesRef.current = uploadedImages;

	// コンポーネントアンマウント時にオブジェクトURLをクリーンアップ
	useEffect(() => {
		return () => {
			for (const img of uploadedImagesRef.current) {
				URL.revokeObjectURL(img.preview);
			}
		};
	}, []);

	const handleImageUpload = useCallback((files: File[]): string => {
		const newImages = files.map((file) => ({
			id: crypto.randomUUID(),
			file,
			preview: URL.createObjectURL(file),
		}));

		setUploadedImages((prev) => [...prev, ...newImages]);

		// 画像タグの文字列を返す
		return newImages.map((img) => `<img src="{{image-${img.id}}}" />`).join("");
	}, []);

	const removeUploadedImage = useCallback((id: string) => {
		setUploadedImages((prev) => {
			const image = prev.find((img) => img.id === id);
			if (image) {
				URL.revokeObjectURL(image.preview);
			}
			return prev.filter((img) => img.id !== id);
		});
	}, []);

	const clearUploadedImages = useCallback(() => {
		for (const img of uploadedImagesRef.current) {
			URL.revokeObjectURL(img.preview);
		}
		setUploadedImages([]);
	}, []);

	return {
		uploadedImages,
		handleImageUpload,
		removeUploadedImage,
		clearUploadedImages,
	};
};
